/**
 * Realtime run streaming (prompt.md §48, §49, §76, §77).
 *
 * WHY the transport is abstracted: the same run must be watchable from three
 * runtimes with different capabilities — a browser (WebSocket), a Tauri shell
 * (WebSocket), and Android (WebSocket or an HTTP fallback when a socket cannot
 * be held open in the background). The protocol guarantee is what stays fixed:
 *
 *  1. resume from a sequence number, never from a fresh start,
 *  2. replay what was missed, then stream live,
 *  3. reconnect with exponential backoff *and* jitter,
 *  4. de-duplicate by event id so a replay never double-applies an event.
 *
 * Agent execution never depends on this socket existing (prompt.md §49): if the
 * client disappears the run continues in the cloud and the next connection
 * replays from `lastSequence`.
 */

import { type Run, type RunId, isTerminal, unsafeId } from "@dayvise/domain";
import type { ResumeResponse, RunStreamEvent } from "@dayvise/event-protocol";

import { isTerminalRunEvent, normalizeRunEvent } from "./events.ts";
import { DayviseApiError } from "./errors.ts";
import { DEFAULT_RETRY_POLICY, delay, retryDelay, type RetryPolicy } from "./retry.ts";

export type RealtimeState = "idle" | "connecting" | "live" | "reconnecting" | "closed" | "exhausted";

export interface RealtimeStateChange {
  readonly state: RealtimeState;
  readonly runId: RunId | null;
  /** Zero on the first connection; increments per failed attempt. */
  readonly attempt: number;
  /** Highest sequence the client has accepted so far. */
  readonly lastSequence: number;
  /** Present for `reconnecting`/`exhausted`, for a visible "reconnecting" hint. */
  readonly error: string | null;
}

/** What a transport hands back while a session is open. */
export interface RunTransportHandlers {
  readonly onEvent: (event: RunStreamEvent) => void;
  /** The server can no longer replay the requested window; refetch a snapshot. */
  readonly onSnapshotRequired: (latestSequence: number) => void;
  /** The session is established (possibly after replaying missed events). */
  readonly onOpen: () => void;
  /** A non-fatal session error, surfaced to state observers. */
  readonly onError: (error: Error) => void;
}

export interface RunTransportContext {
  readonly runId: RunId;
  /** The client's cursor: "give me everything after this". */
  readonly afterSequence: number;
  readonly signal: AbortSignal;
}

export interface RunTransport {
  readonly kind: string;
  /**
   * Runs exactly one session and settles when it ends: resolve on a clean end
   * (run finished, or the session was aborted), reject to request a reconnect.
   */
  open(context: RunTransportContext, handlers: RunTransportHandlers): Promise<void>;
}

/* ------------------------------ websockets -------------------------------- */

/** The slice of the WebSocket API this module uses, so tests can fake it. */
export interface WebSocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  addEventListener(type: "open" | "close" | "error" | "message", listener: (event: { data?: unknown; code?: number; reason?: string }) => void): void;
}

export interface WebSocketTransportConfig {
  /**
   * Absolute socket URL for a run and cursor. Supplied by the application from
   * its `EndpointConfig`, so no host is hardcoded in the SDK (prompt.md §39).
   */
  readonly url: (runId: RunId, afterSequence: number) => string;
  /** Injectable for tests; defaults to the platform `WebSocket`. */
  readonly socketFactory?: (url: string) => WebSocketLike;
}

function defaultSocketFactory(url: string): WebSocketLike {
  const Ctor = (globalThis as { WebSocket?: new (url: string) => WebSocketLike }).WebSocket;
  if (!Ctor) throw new DayviseApiError({ status: 0, code: "network_error", message: "this client has no WebSocket support", retryable: false });
  return new Ctor(url);
}

/**
 * Socket session.
 *
 * The subscription is sent on `open` rather than baked into the query string, so
 * the same socket can be re-subscribed without a reconnect, and the resume
 * cursor travels with the subscription where it cannot be lost by a proxy.
 */
export function createWebSocketTransport(config: WebSocketTransportConfig): RunTransport {
  const socketFactory = config.socketFactory ?? defaultSocketFactory;
  return {
    kind: "websocket",
    open(context, handlers) {
      return new Promise<void>((resolve, reject) => {
        let settled = false;
        // Mirrors the polling transport above. A resolved session tells the outer loop
        // the run ended, so it must only be true when a terminal event actually arrived.
        let sawTerminal = false;
        // A close this client asked for is a clean end, not a dropped transport. Set
        // BEFORE closing, because closing can deliver the close event synchronously,
        // which would otherwise reject the abort before onAbort reaches its own finish.
        let clientClosed = false;
        let socket: WebSocketLike;
        try {
          socket = socketFactory(config.url(context.runId, context.afterSequence));
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
          return;
        }
        const finish = (error?: Error): void => {
          if (settled) return;
          settled = true;
          context.signal.removeEventListener("abort", onAbort);
          if (error) reject(error);
          else resolve();
        };
        const onAbort = (): void => {
          clientClosed = true;
          try {
            socket.close(1000, "client closed");
          } catch {
            // A socket that is already gone needs no further action.
          }
          finish();
        };
        const endTerminal = (): void => {
          clientClosed = true;
          try { socket.close(1000, "run finished"); } catch { /* Already closed. */ }
          finish();
        };
        context.signal.addEventListener("abort", onAbort, { once: true });

        socket.addEventListener("open", () => {
          socket.send(JSON.stringify({ kind: "subscribe", runId: context.runId, afterSequence: context.afterSequence }));
          handlers.onOpen();
        });
        socket.addEventListener("message", (message) => {
          if (settled) return;
          if (typeof message.data !== "string") return;
          let decoded: unknown;
          try {
            decoded = JSON.parse(message.data);
          } catch {
            return;
          }
          const envelope = decoded as { kind?: unknown };
          if (envelope.kind === "event") {
            const event = normalizeRunEvent((decoded as { event?: unknown }).event);
            if (event) {
              if (isTerminalRunEvent(event)) sawTerminal = true;
              handlers.onEvent(event);
              if (sawTerminal) endTerminal();
            }
            return;
          }
          if (envelope.kind === "resume") {
            const response = (decoded as { response?: ResumeResponse }).response;
            if (!response) return;
            for (const raw of response.events) {
              const event = normalizeRunEvent(raw);
              if (!event) continue;
              if (isTerminalRunEvent(event)) sawTerminal = true;
              handlers.onEvent(event);
            }
            if (response.snapshotRequired) handlers.onSnapshotRequired(response.latestSequence);
            if (sawTerminal) endTerminal();
            return;
          }
          if (envelope.kind === "error") {
            const detail = decoded as { code?: unknown; message?: unknown };
            handlers.onError(new DayviseApiError({
              status: 0,
              code: typeof detail.code === "string" ? detail.code : "realtime_error",
              message: typeof detail.message === "string" ? detail.message : "the run stream reported an error",
              retryable: true,
            }));
          }
        });
        socket.addEventListener("error", () => {
          finish(new DayviseApiError({ status: 0, code: "network_error", message: "the run stream disconnected", retryable: true }));
        });
        socket.addEventListener("close", (event) => {
          // A resolved session means "the run ended", and the outer loop stops on it
          // instead of reconnecting. That is only true when a terminal event arrived, or
          // when this client ended the session itself: the server never closes a
          // subscribed socket, so a close frame from an edge drain or an idle timeout
          // arrives mid-run and no code alone can be told apart from a finished run.
          // Trusting a bare 1000 here is what lost events -- the run was still executing
          // and the client simply stopped following it, with no error surfaced and no
          // reconnect. Anything else rejects, and the backoff resumes from the last
          // sequence seen.
          if (sawTerminal || clientClosed) {
            finish();
            return;
          }
          finish(
            new DayviseApiError({
              status: 0,
              code: "network_error",
              message: `the run stream closed before the run finished (code ${event.code ?? "unknown"})`,
              retryable: true,
            }),
          );
        });
      });
    },
  };
}

/* -------------------------------- polling --------------------------------- */

/** Reads a window of the durable event stream. `runs.events` already matches it. */
export interface RunEventsReader {
  (runId: RunId, afterSequence: number, signal: AbortSignal): Promise<ResumeResponse>;
}

/** Reads the authoritative run record, used to end polling for a finished run. */
export interface RunReader {
  (runId: RunId, signal: AbortSignal): Promise<Run | null>;
}

export interface PollingTransportConfig {
  readonly readEvents: RunEventsReader;
  /**
   * Optional but strongly recommended: without it a poll loop started after the
   * run finished has no way to learn that no further events are coming.
   */
  readonly readRun?: RunReader;
  readonly intervalMs?: number;
  readonly sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
}

/**
 * Long-poll fallback used when a socket cannot be held open.
 *
 * It is deliberately not a busy loop: each iteration asks for
 * `after = lastSequence` and then waits, so a run that produces nothing for
 * minutes costs one request per interval rather than per event.
 */
export function createPollingTransport(config: PollingTransportConfig): RunTransport {
  const intervalMs = config.intervalMs ?? 3_000;
  const sleep = config.sleep ?? delay;
  return {
    kind: "polling",
    async open(context, handlers) {
      let cursor = context.afterSequence;
      let opened = false;
      while (!context.signal.aborted) {
        const response = await config.readEvents(context.runId, cursor, context.signal);
        if (!opened) {
          opened = true;
          handlers.onOpen();
        }
        if (response.snapshotRequired) {
          handlers.onSnapshotRequired(response.latestSequence);
          // The window before `latestSequence` can no longer be replayed, so the
          // cursor jumps forward; the caller refetches a snapshot instead.
          cursor = Math.max(cursor, response.latestSequence);
        }
        let sawTerminal = false;
        for (const raw of response.events) {
          const event = normalizeRunEvent(raw);
          if (!event) continue;
          if (event.sequence <= cursor) continue;
          cursor = event.sequence;
          handlers.onEvent(event);
          if (isTerminalRunEvent(event)) sawTerminal = true;
        }
        if (sawTerminal) return;
        // A completed run may still have an unread final message. Drain its
        // replay window before closing, and recover state if no terminal event
        // remains (for example after retention or legacy malformed events).
        if (config.readRun) {
          const run = await config.readRun(context.runId, context.signal);
          if (run && isTerminal(run.state)) {
            if (!response.snapshotRequired) handlers.onSnapshotRequired(response.latestSequence);
            return;
          }
        }
        await sleep(intervalMs, context.signal);
      }
    },
  };
}

/* ------------------------------ stream client ----------------------------- */

export interface RunStreamConfig {
  readonly transport: RunTransport;
  readonly retry?: Partial<RetryPolicy>;
  /** Injected for deterministic backoff in tests. */
  readonly random?: () => number;
  readonly sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
  /** Stops reconnecting after this many consecutive failures. */
  readonly maxAttempts?: number;
  readonly onEvent?: (event: RunStreamEvent) => void;
  /** Set so a `run.error` event can drive the UI without a separate listener. */
  readonly onErrorEvent?: (error: { errorClass: string; message: string; retryable: boolean }) => void;
}

export interface ConnectInput {
  readonly runId: RunId | string;
  /** Resume point. Defaults to -1, meaning "from the beginning of the buffer". */
  readonly afterSequence?: number;
}

export interface RunStream {
  connect(input: ConnectInput): Promise<void>;
  onEvent(listener: (event: RunStreamEvent) => void): () => void;
  onStateChange(listener: (change: RealtimeStateChange) => void): () => void;
  onSnapshotRequired(listener: (latestSequence: number) => void): () => void;
  /** Highest accepted sequence; pass it as `afterSequence` when reconnecting later. */
  lastSequence(): number;
  currentState(): RealtimeState;
  close(): void;
}

/** Memory bound for the duplicate-suppression index. */
const MAX_SEEN_EVENT_IDS = 5_000;

/**
 * The run stream client.
 *
 * Duplicate suppression is belt-and-braces on purpose (prompt.md §15, §77): an
 * event is dropped if its id was already seen, and again if its sequence is not
 * greater than the cursor. Either check alone would let a reconnect re-apply an
 * assistant delta and corrupt the transcript.
 */
export function createRunStream(config: RunStreamConfig): RunStream {
  const retry: RetryPolicy = { ...DEFAULT_RETRY_POLICY, ...config.retry };
  const random = config.random ?? Math.random;
  const sleep = config.sleep ?? delay;
  const maxAttempts = config.maxAttempts ?? Number.POSITIVE_INFINITY;

  const eventListeners = new Set<(event: RunStreamEvent) => void>();
  const stateListeners = new Set<(change: RealtimeStateChange) => void>();
  const snapshotListeners = new Set<(latestSequence: number) => void>();

  const seenEventIds = new Set<string>();
  let lastSequence = -1;
  let state: RealtimeState = "idle";
  let activeRunId: RunId | null = null;
  let controller: AbortController | null = null;

  function remember(eventId: string): void {
    seenEventIds.add(eventId);
    if (seenEventIds.size <= MAX_SEEN_EVENT_IDS) return;
    const oldest = seenEventIds.values().next();
    if (!oldest.done) seenEventIds.delete(oldest.value);
  }

  function emitState(next: RealtimeState, attempt: number, error: string | null): void {
    state = next;
    const change: RealtimeStateChange = { state: next, runId: activeRunId, attempt, lastSequence, error };
    for (const listener of stateListeners) listener(change);
  }

  function accept(raw: RunStreamEvent): boolean {
    if (seenEventIds.has(raw.eventId)) return false;
    if (raw.sequence <= lastSequence) {
      remember(raw.eventId);
      return false;
    }
    remember(raw.eventId);
    lastSequence = raw.sequence;
    return true;
  }

  async function attemptOnce(runId: RunId, signal: AbortSignal): Promise<void> {
    await config.transport.open(
      { runId, afterSequence: lastSequence, signal },
      {
        onOpen: () => emitState("live", 0, null),
        onSnapshotRequired: (latest) => {
          lastSequence = Math.max(lastSequence, latest);
          for (const listener of snapshotListeners) listener(latest);
        },
        onError: (error) => emitState("reconnecting", 0, error.message),
        onEvent: (raw) => {
          if (!accept(raw)) return;
          if (raw.type === "run.error") {
            const payload = raw.payload as { errorClass?: unknown; message?: unknown; retryable?: unknown };
            config.onErrorEvent?.({
              errorClass: typeof payload.errorClass === "string" ? payload.errorClass : "transient",
              message: typeof payload.message === "string" ? payload.message : "the run reported an error",
              retryable: payload.retryable !== false,
            });
          }
          config.onEvent?.(raw);
          for (const listener of eventListeners) listener(raw);
        },
      },
    );
  }

  return {
    async connect(input) {
      const runId = unsafeId<"run">(String(input.runId));
      activeRunId = runId;
      lastSequence = input.afterSequence ?? -1;
      seenEventIds.clear();
      controller?.abort();
      controller = new AbortController();
      const signal = controller.signal;

      let attempt = 0;
      while (!signal.aborted) {
        emitState(attempt === 0 ? "connecting" : "reconnecting", attempt, null);
        try {
          await attemptOnce(runId, signal);
          if (signal.aborted) break;
          // A session that ends without an abort means the run produced its
          // terminal event and the server closed the stream.
          emitState("closed", attempt, null);
          return;
        } catch (error) {
          if (signal.aborted) break;
          const message = error instanceof Error ? error.message : String(error);
          attempt += 1;
          if (attempt >= maxAttempts) {
            emitState("exhausted", attempt, message);
            return;
          }
          emitState("reconnecting", attempt, message);
          try {
            await sleep(retryDelay(attempt - 1, retry, random), signal);
          } catch {
            break;
          }
        }
      }
      emitState("closed", attempt, null);
    },

    onEvent(listener) {
      eventListeners.add(listener);
      return () => eventListeners.delete(listener);
    },

    onStateChange(listener) {
      stateListeners.add(listener);
      return () => stateListeners.delete(listener);
    },

    onSnapshotRequired(listener) {
      snapshotListeners.add(listener);
      return () => snapshotListeners.delete(listener);
    },

    lastSequence: () => lastSequence,
    currentState: () => state,

    close() {
      controller?.abort();
      controller = null;
      emitState("closed", 0, null);
    },
  };
}
